import { Container } from 'aurelia-dependency-injection';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	bindingMode,
	computedFrom,
	newInstance,
	TaskQueue,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import {
	ControllerValidateResult,
	getPropertyInfo,
	ValidateResult,
	ValidationController,
} from 'aurelia-validation';
import debounce from 'lodash-es/debounce';

import { ClassicFormsApi } from '~/api/classic-forms-api';
import { APIParameters } from '~/api/generic-api';
import { CustomisationConfig } from '~/components/customisation/customisation-config';
import { CustomKeysDialog } from '~/components/dialog/custom-keys-dialog';
import { SimpleDialogType } from '~/components/dialog/simple-dialog';
import { EndFieldEventData, Field } from '~/components/field/common/field';
import { Form, FormType } from '~/components/form/form';
import { Notification } from '~/components/notification/notification';
import { DEBOUNCE_RECORD_SELECT_DURATION, ShortcutKey } from '~/constants';
import { Entity } from '~/models/column-definition';
import { FieldModel, FormModel, Modifiable } from '~/models/form-model';
import { ClassicPanelParameter } from '~/models/form-parameters';
import { InsertResult, isInsertResult } from '~/models/insert-result';
import {
	InsertUpdateResult,
	isInsertUpdateResult,
} from '~/models/insert-update-result';
import { BlockUtils } from '~/utils/block-utils';
import { DialogUtils } from '~/utils/dialog-utils';
import { DirtyCheckAggregator } from '~/utils/dirty-check-aggregator';
import { FetchUtils, HttpMethod } from '~/utils/fetch-utils';
import { FocusTrap } from '~/utils/focus-trap';
import FormModelUtils from '~/utils/form-model-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { DomUtils, LockUntilReady, ObjectUtils } from '~/utils/utils';
import { ValidationMessageRenderer } from '~/validation/validation-message-renderer';

export interface ClassicPanelEventData {
	/**
	 * Form Function name
	 */
	source?: string;
	data?: any;
}

export enum FieldIdentifier {
	FIRST,
	LAST,
}

/**
 * Classic form field panel
 */
@autoinject
export class ClassicPanel {
	public static readonly Events = {
		Attached: 'ClassicPanel.Attached',
		PostPopulate: 'ClassicPanel.PostPopulate',
		PostSetIsEditing: 'ClassicPanel.PostSetIsEditing',
	};

	private static idCounter = 1;
	private panelId: number = ClassicPanel.idCounter++;

	public waitUntilFunctionNameSet: LockUntilReady = new LockUntilReady();

	private jQueryReference: JQuery<HTMLElement>;

	public cleanModel: FormModel;

	/**
	 * _formModel has an underscore so when we set a new formModel, we can trigger additional
	 * behaviour, see "set formModel()" below.
	 */
	private _formModel: FormModel;

	/**
	 * _isInEditMode defaults to true so "Edit" modals in client-summary work as expected
	 */
	private _isInEditMode: boolean = true;

	@bindable({
		changeHandler: 'functionNameChanged',
	})
	public functionName: string | (() => string);

	@bindable public recordId: number;
	@bindable public validationController: ValidationController;
	@bindable public type: FormType;
	public extraReadOnlyParameters: string[];
	public preSelectedParameters: ClassicPanelParameter[] = [];

	/**
	 * Optional, formModel is generated from it without API call.
	 */
	@bindable({
		changeHandler: 'tableDataSetChanged',
	})
	public tableDataSet: Array<Entity<any>> | undefined;

	private tableDataSetChanged() {
		this.populateWithClassicFormData(this.getFunctionName(), this.recordId);
	}

	@bindable public entity: Entity<any>;
	// This is used to remove the fields that are present in db but not controlled by user eg 'modified time', 'modified
	// by, 'IPc_Model_Flags2' etc and so should not be part of insert statement.
	private fieldsToFilter: string[] = [];

	@bindable public apiOverride: ClassicFormsApi;

	/**
	 * customisationConfig is used by most forms to apply additional behaviour.
	 *
	 * Set explicitly to false to disable this behaviour. Majority of cases we want this to be
	 * enabled and fall-over if the user forgot to set this.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	public customisationConfig: CustomisationConfig | false | undefined;

	private eventSubscriptions: Subscription[] = [];

	@computedFrom('apiOverride')
	get classicFormsApi(): ClassicFormsApi {
		if (this.apiOverride) {
			return this.apiOverride;
		}
		return this.classicFormsApiInternal;
	}

	private static readonly MODIFICATION_FIELD_ENDINGS: string[] = [
		'z_Mod_User',
		'd_Mod_Date',
		't_Mod_Time',
		'n_US_ID_mod',
		'k_Mod_Timestamp',
	];
	private queryParams: Map<string, string>;

	private readonly dirtyCheckCallback;

	constructor(
		@newInstance()
		private validationMessageRenderer: ValidationMessageRenderer,
		private classicFormsApiInternal: ClassicFormsApi,
		private taskQueue: TaskQueue,
		private element: Element,
		public i18n: I18N,
		protected readonly eventAggregator: EventAggregator,
		readonly dialogUtils: DialogUtils,
		private dirtyCheckAggregator: DirtyCheckAggregator,
	) {
		this.dirtyCheckCallback = dirtyCheckAggregator.registerDirtyCheck(() =>
			this.checkPanelForChanges(),
		);
	}

	public functionNameChanged(newValue, oldValue) {
		if (newValue) {
			this.waitUntilFunctionNameSet.triggerOnce();
		}
	}

	// TODO: can we avoid using this as it is difficult to track when this fires unless events like
	// POST_POPULATE are used. Prefer to manually call populateWithClassicFormData() so we
	// can reliably do things with the form model in the returned Promise
	// this method was observing method for property recordId,
	// it'll be called whenever recordId value changed.
	private recordIdChanged(newValue, oldValue) {
		if (newValue && newValue !== this.recordIdSilent) {
			this.waitUntilFunctionNameSet.then(() =>
				this.populateWithClassicFormDataDebounced(),
			);
			this.recordIdSilent = undefined;
		}
	}

	private recordIdSilent: number;

	/**
	 * Function to set recordId without triggering recordIdChanged (which would cause an AJAX call to get the record).
	 */
	public setRecordIdSilently(recordId: number) {
		this.recordIdSilent = recordId;
		this.recordId = recordId;
	}

	public attached() {
		if (this.validationController) {
			this.validationController.addRenderer(
				this.validationMessageRenderer,
			);
		}

		this.eventAggregator.publish(ClassicPanel.Events.Attached, {
			panel: this,
		});

		this.jQueryReference = $(this.element) as JQuery<HTMLElement>;

		// existing shortcuts from classic forms see: https://service.financialsynergy.com.au/pages/viewpage.action?pageId=40109606
		KeyboardShortcut.subscribe(this, ShortcutKey.FocusFirstField, () =>
			this.focusFirstField(),
		);
		KeyboardShortcut.subscribe(this, ShortcutKey.FocusLastField, () =>
			this.focusLastField(),
		);
	}

	protected detached() {
		KeyboardShortcut.unsubscribeAll(this);
		this.dirtyCheckAggregator.removeDirtyCheck(this.dirtyCheckCallback);
	}

	private bind(bindingContext, overrideContext): void {
		this.eventSubscriptions.push(
			this.eventAggregator.subscribe(
				Field.Events.EndFieldTriggered,
				(data: EndFieldEventData) => {
					if (
						this.formModel &&
						this.formModel.fields[data.model.name] === data.model &&
						!data.isFieldReadonly &&
						data.model.endField
					) {
						this.doEndFieldWithValidation(
							this.formModel,
							data.model.name,
							data.relatedTarget,
						);
					}
				},
			),
		);
	}

	public unbind() {
		this.clearValidation();
		if (this.validationController) {
			this.validationController.reset();
			this.validationController.removeRenderer(
				this.validationMessageRenderer,
			);
		}
		this.dialogUtils.closeAll();
		for (const subscription of this.eventSubscriptions) {
			subscription.dispose();
		}
		this.eventSubscriptions = [];
	}

	public promptToClosePanelIfChangedAndClearValidation(
		hasChanges?: boolean,
	): Promise<void> {
		const hasPermissionToClose =
			this.promptToClosePanelIfChanged(hasChanges);
		this.clearValidation();
		return hasPermissionToClose;
	}

	get formModel() {
		return this._formModel;
	}

	set formModel(formModel: FormModel) {
		if (this._formModel === formModel) {
			return;
		}
		if (!formModel || !formModel.fields) {
			throw new Error('Missing formModel.fields property.');
		}
		this.clearValidation();
		FormModelUtils.formatNullValues(formModel);
		FormModelUtils.applyFieldConfigToFormModel(
			formModel,
			this.customisationConfig,
			this.type === FormType.ADD,
		);
		ClassicPanel.applyPreSelectedParameters(
			formModel,
			this.preSelectedParameters,
			this.type,
		);
		ClassicPanel.applyExtraReadOnlyParameters(
			formModel,
			this.extraReadOnlyParameters,
		);
		this.cleanModel = FormModelUtils.cloneModel(formModel);
		this._formModel = formModel;
		this.setIsEditing(this._isInEditMode);
	}

	private static applyPreSelectedParameters(
		formModel: FormModel,
		preSelectedParameters: ClassicPanelParameter[],
		formType: FormType,
	) {
		if (preSelectedParameters != null) {
			preSelectedParameters.forEach((param) => {
				const field = formModel.fields[param.name];
				if (!field) {
					return;
				}
				if (field.value !== param.value) {
					Logger.log(
						param.name + ' value had to be set on the panel',
					);
					field.value = param.value;
				}
				field.modifiable = Modifiable.FALSE;

				// Set forceSave to true to ensure preselected parameters are included in
				// save even though they are read only and not recognised as a changed field
				if (formType !== FormType.EDIT) {
					field.forceSave = true;
				}
			});
		}
	}

	private static applyExtraReadOnlyParameters(
		formModel: FormModel,
		extraReadOnlyParameters: string[],
	) {
		if (extraReadOnlyParameters != null) {
			extraReadOnlyParameters.forEach((fieldName) => {
				formModel.fields[fieldName].modifiable = Modifiable.FALSE;
			});
		}
	}

	private clearValidation() {
		if (this.validationController) {
			this.validationController.reset();
		}
	}

	/**
	 * haveFieldsChanged will check the current formModel against its state when
	 * we first loaded the form. If values don't match, then the form has changed.
	 */
	private haveFieldsChanged(): boolean {
		let haveFieldsChanged = ClassicPanel.haveFieldsChangedForFormModel(
			this.cleanModel,
			this.formModel,
		);

		const formModelList = this.formModel
			? this.formModel.formModelList
			: undefined;
		const cleanFormModelList = this.cleanModel
			? this.cleanModel.formModelList
			: undefined;
		if (!formModelList) {
			return haveFieldsChanged;
		} else if (!haveFieldsChanged) {
			if (formModelList.length !== cleanFormModelList.length) {
				return true;
			}
			for (const index in formModelList) {
				if (!formModelList.hasOwnProperty(index)) {
					continue;
				}
				haveFieldsChanged = ClassicPanel.haveFieldsChangedForFormModel(
					cleanFormModelList[index],
					formModelList[index],
				);
				if (haveFieldsChanged) {
					break;
				}
			}
		}

		return haveFieldsChanged;
	}

	private static haveFieldsChangedForFormModel(
		cleanModel: FormModel,
		possiblyDirtyModel: FormModel,
	): boolean {
		if (cleanModel && cleanModel.fields) {
			const cleanFields = cleanModel.fields;
			for (const fieldName in cleanFields) {
				if (
					this.isFieldDirty(fieldName, cleanModel, possiblyDirtyModel)
				) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * isFieldDirty is used to detect if values of a field have changed only.
	 * This is used to detect if you have unsaved changes on a form before leaving as
	 * well as used for save validation.
	 */
	private static isFieldDirty(
		fieldName: string,
		cleanModel: FormModel,
		possiblyDirtyModel: FormModel,
	): boolean {
		if (!possiblyDirtyModel.fields.hasOwnProperty(fieldName)) {
			return false;
		}
		const fieldModel = possiblyDirtyModel.fields[fieldName];
		if (fieldModel.excludeFromSave === true) {
			return false;
		}

		if (fieldModel.modifiable === Modifiable.FALSE) {
			return false;
		}

		// Detect if the value has changed
		return FormModelUtils.isFieldChanged(
			fieldName,
			cleanModel,
			possiblyDirtyModel,
		);
	}

	public static logAndShowPreInsertError(error: Error): void {
		const errorMessage = Container.instance
			.get(I18N)
			.tr('errors.pre-insert', {
				message: FetchUtils.normalizeError(error),
			});
		FetchUtils.logAndDisplayError(errorMessage);
	}

	public populateWithPreInsertClassicFormData(
		preEnableInputCallback?: (model: FormModel) => void,
		functionName?: string,
		params: APIParameters = {},
	): Promise<FormModel> {
		const functionNameUsed = functionName
			? functionName
			: this.getFunctionName();
		return ClassicPanel.getPreInsertFormModelPromise(
			this.classicFormsApi,
			functionNameUsed,
			params,
			preEnableInputCallback,
		)
			.then((model: FormModel) => {
				this.processPreInsertFormModel(model);
				FormModelUtils.showWarningMessagesIfPresent(model);
				return model;
			})
			.catch((error) => {
				this.setIsEditing(false);

				ClassicPanel.logAndShowPreInsertError(error);

				// NOTE: Jake: 2019-01-09
				// Its a bit of an anti-pattern to half-handle the error here and also
				// propogate the error again for more handling in "abstract-transaction-form".
				//
				// Please don't copy this pattern. It'd be better to make those two areas share
				// the base code and then you compose more rules on-top.
				return Promise.reject(error);
			});
	}

	/**
	 * This is static so that FormModel data can be retrieved from the api in an 'activate' method before this Panel
	 * exists.
	 * It can then be provided to the panel later after it has been created/bound.
	 */
	public static getPreInsertFormModelPromise(
		classicFormsApi: ClassicFormsApi,
		functionName: string,
		params: APIParameters = {},
		preEnableInputCallback?: (model: FormModel) => void,
	): Promise<FormModel> {
		Logger.log('Populating with classic form data', functionName, params);
		return classicFormsApi
			.getPreInsertClassicForm(functionName, params)
			.then((model) => {
				if (preEnableInputCallback) {
					preEnableInputCallback(model);
				}
				return model;
			});
	}

	/**
	 * This can be used to provide a pre-loaded pre-insert <code>FormModel</code>
	 */
	public processPreInsertFormModel(formModel: FormModel) {
		this.formModel = formModel;
		this.setIsEditing(true);
		return formModel;
	}

	public readonly populateWithClassicFormDataDebounced = debounce(
		() => {
			this.populateWithClassicFormData(
				this.getFunctionName(),
				this.recordId,
			);
		},
		DEBOUNCE_RECORD_SELECT_DURATION,
		{ leading: true, trailing: true },
	); // Allow the first call to go through immediately. Subsequent calls will be on hold until timeout stops being reset.

	public populateWithClassicFormData(
		functionName: string,
		recordId: number,
	): void {
		// If the recordId changes when the form is in ADD mode, we are (probably?) cloning and don't want to do anything.
		if (this.type === FormType.ADD) {
			return;
		}
		const params: APIParameters =
			this.queryParams?.size > 0
				? Object.fromEntries(this.queryParams)
				: undefined;
		ClassicPanel.getFormModel(
			this.classicFormsApi,
			functionName,
			recordId,
			this.entity,
			this.tableDataSet,
			params,
		)
			.then((model) => {
				this.processEditFormModel(model);
				FormModelUtils.showWarningMessagesIfPresent(model);
			})
			.catch((error) => {
				this.setIsEditing(false);

				const errorMessage = Container.instance
					.get(I18N)
					.tr('errors.load-record', {
						id: recordId,
						message: FetchUtils.normalizeError(error),
					});
				FetchUtils.logAndDisplayError(errorMessage);
			});
	}

	/**
	 * This is static so that FormModel data can be retrieved from the api in an 'activate' method before this Panel
	 * exists.
	 * It can then be provided to the panel later after it has been created/bound.
	 */
	public static getFormModel(
		classicFormsApi: ClassicFormsApi,
		functionName: string,
		recordId: number,
		entity?: Entity<any>,
		tableDataSet?: Array<Entity<any>>,
		params?: APIParameters,
	): Promise<FormModel> {
		if (entity) {
			// if entity is declared through record-modal, use this to set formModel (enables use of custom fields)
			return Promise.resolve(this.makeFormModelFromEntity(entity));
		} else if (tableDataSet) {
			// if array of entities are declared through slideout, use these and recordId to set formModel (enables use of custom fields)
			let matchingEntity: Entity<any> = {};
			for (const e of tableDataSet) {
				if (FormModelUtils.getIdFromIdentityField(e) === recordId) {
					matchingEntity = e;
					break;
				}
			}
			// NOTE: York: 2021-07-02 - WEBUI-2158
			// Should return clone as field models properties (i.e, modifiable) in this form model is
			// mutated somewhere (see `setFieldsToReadonly`) and no API is called to fetch a clean form model
			// like classicFormsApi.getClassicFormData.
			return Promise.resolve(
				FormModelUtils.cloneModel(
					this.makeFormModelFromEntity(matchingEntity),
				),
			);
		} else {
			// otherwise, use declared functionName and recordId to call api and retrieve formModel
			return classicFormsApi.getClassicFormData(
				functionName,
				recordId,
				params,
			);
		}
	}

	private static makeFormModelFromEntity(entity: Entity<any>): FormModel {
		// NOTE: Lucas: 12/12/2019 - WEBUI-1523
		// Treating an entity as a {[key: string]: FieldModel} will not work robustly as an entity can have another
		// entity as a member (which cannot be assigned to FieldModel).
		// However, currently, all entities that are fed into this just have ColumnValue fields so we have not hit this
		// problem as of yet.
		const fields = entity as { [key: string]: FieldModel };
		return FormModelUtils.makeFormModelWith(fields);
	}

	/**
	 * processEditFormModel is called by classic-panel/classic-panel-dialog to set
	 * the formModel up for existing records.
	 */
	public processEditFormModel(formModel: FormModel) {
		// NOTE: Jake: 2019-06-11 - WEBUI-2075
		// Don't change "recordId" here as it will trigger an AJAX request loop
		// due to the recordIdChanged() callback.
		// this.recordId = recordId;
		this.formModel = formModel;
		this.eventAggregator.publish(ClassicPanel.Events.PostPopulate, {
			data: this,
			source: this.functionName,
		});
	}

	private static clearFields(
		formModel: FormModel,
		clearFilter: (formModel: FormModel, fieldName: string) => boolean,
	): FormModel {
		for (const fieldName in formModel.fields) {
			if (clearFilter(formModel, fieldName)) {
				delete formModel.fields[fieldName];
			}
		}
		return formModel;
	}

	private static fieldInFieldEndings(
		fieldName: string,
		endings: string[],
	): boolean {
		return endings.some((ending) => fieldName.endsWith(ending));
	}

	public static clearInvisibleReadonlyFields(formModel: FormModel) {
		return this.clearFields(
			formModel,
			(model, fieldName) =>
				model.fields[fieldName].modifiable === Modifiable.FALSE ||
				model.fields[fieldName].visible ||
				this.fieldInFieldEndings(
					fieldName,
					ClassicPanel.MODIFICATION_FIELD_ENDINGS,
				),
		);
	}

	public static clearInvisibleFields(formModel: FormModel) {
		return this.clearFields(
			formModel,
			(model, fieldName) =>
				model.fields[fieldName].visible ||
				this.fieldInFieldEndings(
					fieldName,
					ClassicPanel.MODIFICATION_FIELD_ENDINGS,
				),
		);
	}

	/**
	 * Performs common validations and returns save result form classicforms api for custom processing.
	 * The returned promise will contain undefined if the panel is disabled.
	 */
	public async save(
		customApi?: string,
		customHttpMethod?: HttpMethod,
		formExtension?: string,
	): Promise<FormModel | InsertResult | undefined> {
		// Let blur event fire first to start any end field processing
		await new Promise((resolve) => this.taskQueue.queueTask(resolve));
		await this.waitWhileProcessingEndFieldRequest;

		return await this.saveAndProcessResponse(
			(saveResponse) => Promise.resolve(saveResponse),
			customApi,
			customHttpMethod,
			formExtension,
		);
	}

	/**
	 * If the panel is disabled, this does nothing and the returned promise will contain undefined.
	 * Otherwise, this performs common validations then disables input fields then gets a save result
	 * then call saveResponseProcessor on the result then enables input fields then returns the processed
	 * save result.
	 */
	public async saveAndProcessResponse<T>(
		saveResponseProcessor: (
			saveResult: FormModel | InsertResult,
		) => Promise<T>,
		customApi?: string,
		customHttpMethod?: HttpMethod,
		formExtension?: string,
	): Promise<T | undefined> {
		if (this.isDeleted()) {
			throw new Error(this.i18n.tr('errors.record-deleted'));
		}

		try {
			return await this.validateAndSave(
				saveResponseProcessor,
				customApi,
				customHttpMethod,
				formExtension,
			);
		} catch (errorObject) {
			throw errorObject;
		}
	}

	private async validateAndSave<T>(
		saveResponseProcessor: (
			saveResult: FormModel | InsertResult | InsertUpdateResult,
		) => Promise<T>,
		customApi?: string,
		customHttpMethod?: HttpMethod,
		formExtension?: string,
	): Promise<T> {
		try {
			await this.validateForSave(customApi);
		} catch (errorMessage) {
			Logger.error(errorMessage);
			throw errorMessage;
		}

		const preSaveFormModel = FormModelUtils.cloneModel(this.formModel);
		let postModel: FormModel;
		if (customApi) {
			postModel = ClassicPanel.createModelWithAllFields(this.formModel);
		} else if (this.type === FormType.ADD) {
			postModel = FormModelUtils.createModelWithNonEmptyFields(
				this.formModel,
			);
		} else {
			postModel = FormModelUtils.createModelWithOnlyChangedFields(
				this.formModel,
				this.cleanModel,
			);
			FormModelUtils.applyFieldConfigToFormModel(
				postModel,
				this.customisationConfig,
				true,
			);

			if (
				ObjectUtils.isEmpty(postModel.fields) &&
				ObjectUtils.isEmpty(postModel.formModelList)
			) {
				throw new Error(this.i18n.tr('errors.nothing-to-save-message'));
			}
		}
		const recordIdPrime = this.type === FormType.ADD ? null : this.recordId;

		const saveResult = await this.classicFormsApi.saveClassicFormData(
			this.getFunctionName(),
			postModel,
			customApi,
			formExtension,
			recordIdPrime,
			customHttpMethod,
			this.queryParams,
		);

		FormModelUtils.showWarningMessagesIfPresent(saveResult);

		const returnValue = await saveResponseProcessor(saveResult);

		if (isInsertResult(saveResult) || isInsertUpdateResult(saveResult)) {
			// NOTE (Lucas): 02/12/2019 - WEBUI-468
			// model is not a FormModel when this is an ADD type of panel.
			this.formModel = preSaveFormModel;
		} else {
			this.formModel = saveResult;
		}
		return returnValue;
	}

	public async validateForSave(customApi?: string): Promise<void> {
		const validationResult = await this.validationController.validate();

		if (!validationResult.valid) {
			// validation failed
			throw new Error(
				this.i18n.tr('errors.one-or-more-fields-are-invalid-message'),
			);
		}

		// no need to perform dirty check on form with custom mapping as form is prepopulated and may not have any changes
		// No need to reject a clone attempt
		// A clean and readonly form may contains force save fields. If there are not, throw nothing to save.
		// A clean form that contains modifiable fields definitely should throw nothing to save.
		if (
			!customApi &&
			!this.haveFieldsChanged() &&
			!(this.type === FormType.ADD && this.recordId) &&
			!this.isReadonlyForm() &&
			!this.hasForceSaveOrModifiedByEventFields()
		) {
			throw new Error(this.i18n.tr('errors.nothing-to-save-message'));
		}
	}

	/**
	 * If all fields are not modifiable, return true, otherwise false
	 */
	private isReadonlyForm(): boolean {
		return Object.values(this.formModel.fields).every(
			(field) => field.modifiable === Modifiable.FALSE,
		);
	}

	private hasForceSaveOrModifiedByEventFields(): boolean {
		return Object.values(this.formModel.fields).some(
			(field) => field.forceSave || field.modifiedByEvent,
		);
	}

	private static createModelWithAllFields(formModel: FormModel): FormModel {
		const postModel: FormModel = FormModelUtils.cloneModel(formModel);
		// need to send all fields with values for custom api because form is prepopulated and may not have any changes
		for (const fieldName of Object.keys(postModel.fields)) {
			const field = postModel.fields[fieldName];
			if (field.value === '') {
				delete postModel.fields[fieldName];
			}
		}

		const formModelList = formModel.formModelList;
		if (formModelList) {
			postModel.formModelList = [];
			formModelList.forEach((childFormModel) => {
				const childChangedModel =
					this.createModelWithAllFields(childFormModel);
				postModel.formModelList.push(childChangedModel);
			});
		}

		return postModel;
	}

	public isDeleted(): boolean {
		const field = FormModelUtils.getDeletedField(this.cleanModel);
		if (field) {
			return field.value === 'Y';
		} else {
			return false;
		}
	}

	@computedFrom('_isInEditMode')
	get isInEditMode(): boolean {
		return this._isInEditMode;
	}

	/**
	 * setIsEditing allows you to set whether the fields in the panel are editable or not.
	 * If false, all fields become readonly.
	 */
	public setIsEditing(enable: boolean) {
		this._isInEditMode = enable;

		const formModelFields =
			this.formModel && this.formModel.fields
				? this.formModel.fields
				: [];
		const formModelList = this.formModel
			? this.formModel.formModelList
			: undefined;
		if (formModelFields.length === 0) {
			Logger.log(
				`Set to readonly mode, but no fields are on formModel to modify and make readonly.`,
			);
			return;
		}
		if (enable) {
			this.clearFieldsFromReadonly(formModelFields, this.cleanModel);

			// clear read only on any child form models
			if (formModelList) {
				for (const index in formModelList) {
					if (!formModelList.hasOwnProperty(index)) {
						continue;
					}
					// This can occur on forms such as Member Payment Schedule where adding
					// a child formModel to the parent formModel means the number of child
					// formModels in the formModelList do not match those in the cleanModel
					const cleanChildFormModel =
						this.cleanModel.formModelList[index];
					if (!cleanChildFormModel) {
						continue;
					}
					const childFormModelFields =
						formModelList[index]?.fields ?? [];
					this.clearFieldsFromReadonly(
						childFormModelFields,
						cleanChildFormModel,
					);
				}
			}
		} else {
			this.setFieldsToReadonly(formModelFields);

			// set read only on any child form models
			if (formModelList) {
				formModelList.forEach((childFormModel) => {
					const childFormModelFields =
						childFormModel && childFormModel.fields
							? childFormModel.fields
							: [];
					this.setFieldsToReadonly(childFormModelFields);
				});
			}
		}
		this.eventAggregator.publish(ClassicPanel.Events.PostSetIsEditing, {
			value: enable,
			source: this.functionName,
		});
	}

	private clearFieldsFromReadonly(
		formModelFields: any[] | { [key in string]: FieldModel },
		cleanModel: FormModel,
	) {
		Logger.log('Clearing fields from being readonly.');
		for (const fieldName in formModelFields) {
			if (!formModelFields.hasOwnProperty(fieldName)) {
				continue;
			}
			const field = formModelFields[fieldName];
			if (!field) {
				continue;
			}
			const cleanField = cleanModel.fields[fieldName];
			if (!cleanField) {
				Logger.log(
					"Skipping field '" +
						fieldName +
						"' as it was not in server side formModel",
				);
				continue;
			}
			if (
				this.type === FormType.EDIT &&
				cleanField.modifiable === Modifiable.INSERT_ONLY
			) {
				Logger.log(
					"Skipping field '" +
						fieldName +
						"' on this EDIT form as it is Modifiable only on Insert",
				);
				/*
				 * Make sure if field is insert only and formtype is edit that it
				 * is forced to Modifiable.FALSE as INSERT_ONLY will be
				 * interpreted as TRUE.
				 */
				field.modifiable = Modifiable.FALSE;
				continue;
			}
			field.modifiable = cleanField.modifiable;
		}
	}

	private setFieldsToReadonly(
		formModelFields: any[] | { [key in string]: FieldModel },
	) {
		Logger.warn('Setting fields to readonly.');
		for (const fieldName in formModelFields) {
			if (!formModelFields.hasOwnProperty(fieldName)) {
				continue;
			}
			const field = formModelFields[fieldName];
			if (!field) {
				continue;
			}
			field.modifiable = Modifiable.FALSE;
		}
	}

	public setFunctionName(functionName: string | (() => string)) {
		this.functionName = functionName;
		this.waitUntilFunctionNameSet.triggerOnce();
	}

	public getFunctionName(): string {
		if (typeof this.functionName === 'function') {
			return this.functionName();
		}
		return this.functionName;
	}

	/**
	 * Returns true if any fields have changed
	 */
	public checkPanelForChanges(): boolean {
		// NOTE(Jae): 2020-04-17
		// If we're focused on a form field element, we blur it so that
		// the field value state is updated.
		if (
			document.activeElement instanceof HTMLInputElement ||
			document.activeElement instanceof HTMLSelectElement ||
			document.activeElement instanceof HTMLTextAreaElement
		) {
			document.activeElement.blur();
		}
		return this.haveFieldsChanged();
	}

	/**
	 * Checks if there are any changes. If there are no changes, promise is resolved.
	 * If there are changes displays a confirmation dialog before resolving and clearing changes.
	 * Promise is rejected on cancel.
	 * @param hasChanges provide pre-calculated dirty check result
	 */
	public promptToClosePanelIfChanged(hasChanges?: boolean): Promise<void> {
		const isDirty =
			hasChanges === undefined ? this.checkPanelForChanges() : hasChanges;
		if (!isDirty) {
			return Promise.resolve();
		}

		const dialogModel = this.dialogUtils.getSimpleDialogModel({
			bodyText: this.i18n.tr('slideout.unsaved-changes-will-be-lost-ask'),
			simpleDialogType: SimpleDialogType.WARNING,
		});

		return new Promise((resolve, reject) => {
			this.dialogUtils
				.showCustomDialog(CustomKeysDialog, dialogModel)
				.whenClosed((value) => {
					if (!value.wasCancelled) {
						this.formModel = this.cleanModel;
						resolve();
					} else {
						reject();
					}
				});
		});
	}

	private async doEndFieldWithValidation(
		formModel: FormModel,
		fieldName: string,
		relatedTarget: HTMLElement,
	): Promise<void> {
		if (!formModel || !formModel.fields[fieldName].endField) {
			return;
		}
		const formModelForRequest = FormModelUtils.cloneModel(formModel);

		const controllerValidateResult = await this.validateForEndField(
			fieldName,
		);
		if (!controllerValidateResult.valid) {
			const invalidResults = controllerValidateResult.results.filter(
				(r) => !r.valid,
			);

			if (invalidResults.length > 0) {
				const message = invalidResults
					.map(this.handleFieldValidationError)
					.join('\n');
				Notification.warning(
					this.i18n.tr(
						'warnings.end-field-triggered-with-invalid-values',
						{ message },
					),
				);
			}
			return;
		}
		await this.doEndFieldRequest(
			formModelForRequest,
			fieldName,
			relatedTarget,
		);
	}

	private validateForEndField(
		endFieldName: string,
	): Promise<ControllerValidateResult> {
		const propertyInfosToValidate = Array.from(
			(this.validationController as any).bindings || [],
		)
			.map(([binding]) =>
				getPropertyInfo(binding.sourceExpression, binding.source),
			)
			.filter(
				(propertyInfo) =>
					propertyInfo.object instanceof Field &&
					propertyInfo.object.fieldModel &&
					propertyInfo.object.fieldModel.value &&
					propertyInfo.object.fieldModel.value.trim() !== '',
			);

		return Promise.all(
			propertyInfosToValidate.map((p) =>
				this.validationController.validate({
					object: p.object,
					propertyName: p.propertyName,
				}),
			),
		).then((resultSets) =>
			resultSets.reduce(
				(acc, cur) => {
					acc.results = acc.results.concat(cur.results);
					acc.valid = acc.valid && cur.valid;
					return acc;
				},
				{ results: [], valid: true } as ControllerValidateResult,
			),
		);
	}

	/**
	 * Return message if result has no object and fieldModel binding, otherwise return fieldModel label
	 * and name to indicate which field has error.
	 */
	private handleFieldValidationError(result: ValidateResult): string {
		if (!result.object || !result.object.fieldModel) {
			return result.message;
		}
		const fieldModel = result.object.fieldModel;
		return `${fieldModel.label} (${fieldModel.name})`;
	}

	/**
	 * It is public in case any other classes use it. It is intentional to be used on validate an end field
	 * but validation should applied on all end fields so this is replaced with `validateEndFields`. I reckon
	 * this method may be useful in the future so I haven't removed it. There a number of way you can get the
	 * passed in field parameter.
	 * - From `fieldViewModel` in FieldEventData when such events are published
	 * - By `view-model.ref` reference property
	 * - By `document.querySelector` to get field html element and query from `au.controller.viewModel`
	 *
	 * Compared to `this.validationController.validate`, it only validates the specified field instead of
	 * all fields.
	 * @param field an instance of Field
	 */
	public validateField(field: Field): Promise<ControllerValidateResult> {
		const propertyInfoForField = Array.from(
			(this.validationController as any).bindings || [],
		)
			.map(([binding]) =>
				getPropertyInfo(binding.sourceExpression, binding.source),
			)
			.find((propertyInfo) => propertyInfo.object === field);
		if (!propertyInfoForField) {
			throw new Error(
				`Can't find binding property info for field ${field}.`,
			);
		}
		Logger.debug(
			`Find binding property info for field ${field}: `,
			propertyInfoForField,
		);
		const { object, propertyName } = propertyInfoForField;
		// NOTE (York): we can get the private property validator, e.g, (this.validationController as any).validator.
		// and do `validator.validateProperty(object, propertyName)`. This does just validation and doesn't do error
		// message render.
		return this.validationController.validate({ object, propertyName });
	}

	/**
	 * Catch an in progress 'click' event by removing this at the
	 * 'mousedown' event and re-adding this at the 'mouseup' event.
	 * @param clickEvent EventListener to handle the event
	 */
	private catchInProgressClickEvent(clickEvent: EventListener) {
		// event that holds original click event
		const eventHandler = (e) => {
			clickEvent(e);
			// not needed after firing
			window.removeEventListener('mousedown', mouseDownHandler);
		};

		// NOTE(Albert): 2020-04-23 - WEBUI-3349
		// Previously, this was added to the 'click' event which works fine in the browser (clicking
		// save once waits for end field and then saves) but not in TreeView (clicking save once waits
		// for end field but save needs to be clicked again). Not sure why they are different but I
		// suspect this should be 'mouseup' because the 'click' event incorporates both 'mousedown'
		// and 'mouseup' events so there is/ overlap when adding both a 'mousedown' and 'click' event.
		window.addEventListener('mouseup', eventHandler, { once: true });

		// we only want to catch a click that is in process
		const mouseDownHandler = () =>
			window.removeEventListener('click', eventHandler);
		window.addEventListener('mousedown', mouseDownHandler, { once: true });
	}

	/**
	 * If we are currently processing an end field request, this promise will resolve once the request
	 * has completed. Otherwise it will resolve immediately.
	 */
	private waitWhileProcessingEndFieldRequest: PromiseLike<any> =
		Promise.resolve();

	private async doEndFieldRequest(
		formModel: FormModel,
		fieldName: string,
		relatedTarget: HTMLElement,
	): Promise<void> {
		BlockUtils.toggleAppInput(true); // overlay

		// If the 'blur' triggering this was caused by a mouse click, the overlay we are using to block input catches
		// the 'mouseup' event preventing the 'click' event from firing on the pressed element.
		// This causes issues, for example, when clicking save after leaving an end field, the 'click' won't fire.
		// This attempts to capture the 'click' event higher in the DOM, and re-fire it on the element
		// the user pressed, waiting until after the end-field has completed.
		// This should work correctly with Keyboard Shortcuts. When saving via a keyboard shortcut, the 'click' event
		// is triggered before this 'blur' event. After this, the overlay disables Keyboard shortcuts.
		if (relatedTarget && relatedTarget instanceof HTMLElement) {
			// NOTE(Jae): 2020-03-10 - WEBUI-3165
			// After chatting with Emrys, my understanding of this is that we want to trigger
			// a click event on our save button after the end-field.
			// We can't just call "this.save()" as additional logic currently surrounds the call
			// in our slideout.ts / classic-panel-dialog.ts classes.
			this.catchInProgressClickEvent((event) => {
				// forward the click after endfield processing
				this.waitWhileProcessingEndFieldRequest.then(() => {
					// NOTE(Ben): 2023-01-23 WEBUI-4703
					// If blur event was triggered by tab and move on to a button, the click will be triggered whever
					// click is happening, need an additional check on the cursor position to determine if the user does
					// click on the button
					if (
						DomUtils.isClickingOnTheElementPosition(
							event as MouseEvent,
							relatedTarget,
						)
					) {
						relatedTarget.click();
					}
				});
			});
		}

		let endFieldFormModel: FormModel;
		const waitUntilEndFieldComplete: LockUntilReady = new LockUntilReady();
		this.waitWhileProcessingEndFieldRequest = waitUntilEndFieldComplete;
		try {
			endFieldFormModel = await this.classicFormsApi.getEndFieldFormData(
				this.getFunctionName(),
				formModel,
				fieldName,
				this.queryParams,
			);
			FormModelUtils.showWarningMessagesIfPresent(endFieldFormModel);

			// Force end-field fields to save if their Modifiable.FALSE after getting a response back
			const cleanFieldModelMap = this.cleanModel.fields;
			for (const modelFieldName in endFieldFormModel.fields) {
				if (endFieldFormModel.fields.hasOwnProperty(modelFieldName)) {
					const cleanFieldModel = cleanFieldModelMap[modelFieldName];
					const fieldModel = endFieldFormModel.fields[modelFieldName];

					if (fieldModel && cleanFieldModel) {
						fieldModel.forceSave = cleanFieldModel.forceSave;
					}

					// end field may modify read only fields so enable forceSave to ensure these modified fields are saved
					// ZZ fields should be excluded as they are never saved
					if (
						fieldModel &&
						// NOTE(Jae): 2020-04-17 - WEBUI-3319
						// We need to check if modifiable was previously TRUE and if it was, force save this value.
						// This resolves a bug where changing PPGz_Status from D to B makes the field readonly immediately via
						// an end-field, so it ends up having "no changes".
						(fieldModel.modifiedByEvent ||
							!cleanFieldModel ||
							cleanFieldModel.modifiable === Modifiable.TRUE) &&
						fieldModel.modifiable === Modifiable.FALSE &&
						!fieldModel.name.startsWith(
							FormModelUtils.CUSTOM_FIELD_PREFIX,
						)
					) {
						fieldModel.forceSave = true;
					}
				}
			}

			// Avoid triggering events in "set formModel()"
			FormModelUtils.formatNullValues(endFieldFormModel);
			this._formModel = endFieldFormModel;
		} catch (e) {
			Logger.warn('end-field request failed: ', e);

			// Try to display a nicer looking message to the user (gets rid of the system code stuff at the beginning)
			let msg = FetchUtils.normalizeError(e);
			const searchString = 'Error:';
			const indexOfError = msg.indexOf(searchString);
			if (indexOfError >= 0) {
				msg = msg.substring(indexOfError + searchString.length).trim();
			}
			Notification.error(msg);
		} finally {
			waitUntilEndFieldComplete.triggerOnce();
			BlockUtils.toggleAppInput(false);
		}
	}

	public focusFirstField() {
		// NOTE(Jake): 2018-09-03 - WEBUI-918
		// Wait 1 frame for the elements to appear so we can focus them.
		window.requestAnimationFrame(() => {
			this.focusOnField(FieldIdentifier.FIRST);
		});
		// NOTE(Jake): 2018-09-04 - WEBUI-918
		// Temporary fix.
		// Search fields in search.ts take time to initialize / cause loss of focus on the input.
		// If after a few milliseconds we aren't focused on an input, force focus on the first input.
		// Allow active element to be an input, select or span (eg datepicker icon) without stealing focus.
		// NOTE: Jake: 2019-08-28 - WEBUI-2290
		// This should no longer be needed. At least for Modals and Slideouts.
		// setTimeout(() => {
		// 	const lostInputFocus = document.activeElement &&
		// 		!(document.activeElement.tagName === 'INPUT' ||
		// 		document.activeElement.tagName === 'SELECT' ||
		// 		document.activeElement.tagName === 'SPAN');
		// 	if (lostInputFocus) {
		// 		this.focusOnField(FieldIdentifier.FIRST);
		// 	}
		// }, 1500);
	}

	/**
	 * Called from 'entity-search.html'
	 */
	private focusLastField() {
		this.focusOnField(FieldIdentifier.LAST);
	}

	/**
	 * Only want to focus on a field when classic panel is in edit mode and field is not read only
	 */
	private focusOnField(fieldIdentifier: FieldIdentifier) {
		if (!this.isInEditMode) {
			return;
		}
		const tabbableElements = FocusTrap.getTabbableNodes(
			this.element as HTMLElement,
		);
		if (tabbableElements.length === 0) {
			return;
		}
		const fieldIndex =
			fieldIdentifier === FieldIdentifier.FIRST
				? 0
				: tabbableElements.length - 1;
		const inputToFocus = tabbableElements[fieldIndex];
		if (inputToFocus) {
			inputToFocus.focus();
		}
	}

	public setFilterFields(tableColumnNames: string[]) {
		this.fieldsToFilter = tableColumnNames;
	}

	public getJQueryReference() {
		return this.jQueryReference;
	}

	public setQueryParams(queryParams: Map<string, string>) {
		this.queryParams = queryParams;
	}
}
